import {
  Button,
  Group,
  InputWrapper,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  ActionIcon,
  useMantineTheme,
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Photo, Upload, X, ChevronDown } from 'tabler-icons-react';
import { XlSXIcon } from '../../constants/icon';
import { objectsDefListSelector } from '../../reducers/objectReducers/objectSelector';
import { WrapIconExcelModalImport } from './ModalImportExcelCSS';
import APortal from '../UI/APortal/APortal';
import ObjectSelector from '../object-selector/index.js';

function getIconColor(status, theme) {
  return status.accepted
    ? theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 4 : 6]
    : status.rejected
    ? theme.colors.red[theme.colorScheme === 'dark' ? 4 : 6]
    : theme.colorScheme === 'dark'
    ? theme.colors.dark[0]
    : theme.colors.gray[7];
}

function ImageUploadIcon({ status, ...props }) {
  if (status.accepted) {
    return <Upload {...props} />;
  }

  if (status.rejected) {
    return <X {...props} />;
  }

  return <Photo {...props} />;
}

export const dropzoneChildren = (status, theme, file) => {
  return (
    <Group
      position="center"
      spacing="xl"
      style={{ minHeight: 220, pointerEvents: 'none', height: '100%' }}
    >
      {file ? (
        <WrapIconExcelModalImport>
          <XlSXIcon />
        </WrapIconExcelModalImport>
      ) : (
        <ImageUploadIcon
          status={status}
          style={{ color: getIconColor(status, theme) }}
          size={80}
        />
      )}

      <div>
        <Text size="xl" inline>
          {file ? file?.name : 'Drag excel file here or click to select file'}
        </Text>
      </div>
    </Group>
  );
};

function UpFileTab({
  nextStep,
  valueSelect,
  setValueSelects,
  file,
  setFile,
  isLoading,
  // Nouvelles props pour le framework
  selectedFramework,
  setSelectedFramework,
}) {
  const theme = useMantineTheme();
  const [isSelectingFramework, setIsSelectingFramework] = useState(false);

  const objFormData = useSelector(objectsDefListSelector);
  const isLoadingObjectsDefList = useSelector(
    (state) => state?.objects?.isLoadingObjectsDefList
  );

  // Données pour le premier Select (qu'est-ce qu'on importe)
  const dataSelectDefList = objFormData?.reduce((accumulator, currentValue) => {
    accumulator.push({
      label: currentValue.label,
      value: currentValue.key,
    });
    return accumulator;
  }, []);

  // Vérifier si le type sélectionné est une observation
  const selectedObjectType = objFormData?.find(
    (obj) => obj.key === valueSelect
  );
  const isObservationType = selectedObjectType?.label
    ?.toLowerCase()
    .startsWith('observation');

  // Trouver le type framework pour l'ObjectSelector
  const frameworkType = objFormData?.find(
    (obj) =>
      obj.label?.toLowerCase().includes('framework') ||
      obj.label?.toLowerCase().includes('standard')
  );

  // Trouver l'objet framework sélectionné pour afficher son nom
  const selectedFrameworkObject = selectedFramework
    ? // Ici vous devrez adapter selon comment vous récupérez l'objet par son ID
      { label: `Framework ${selectedFramework}` } // Placeholder - à adapter
    : null;

  const disableNextStep =
    isEmpty(file) || !valueSelect || (isObservationType && !selectedFramework);

  return (
    <Stack style={{ height: '100%' }}>
      {/* Premier champ : Qu'est-ce qu'on importe */}
      <InputWrapper
        required
        label="Select object type to import"
        size="xs"
        styles={{
          root: {
            maxWidth: 300,
          },
        }}
      >
        <Select
          icon={isLoadingObjectsDefList ? <Loader size={16} /> : null}
          value={valueSelect}
          onChange={(value) => {
            setValueSelects(value);
            // Reset framework selection when changing object type
            if (selectedFramework) {
              setSelectedFramework(null);
            }
          }}
          searchable
          clearable
          placeholder="Select object type"
          data={dataSelectDefList}
        />
      </InputWrapper>

      {/* Deuxième champ : Où l'importer (conditionnel) */}
      {isObservationType && (
        <InputWrapper
          required
          label="Select target framework"
          size="xs"
          styles={{
            root: {
              maxWidth: 300,
            },
          }}
        >
          <div style={{ position: 'relative' }}>
            <TextInput
              value={selectedFrameworkObject?.label || ''}
              placeholder="Select framework to import into"
              readOnly
              rightSection={
                <Group spacing={4}>
                  {selectedFramework && (
                    <ActionIcon
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFramework(null);
                      }}
                    >
                      <X size={12} />
                    </ActionIcon>
                  )}
                  <ActionIcon
                    size="sm"
                    onClick={() => setIsSelectingFramework(true)}
                  >
                    <ChevronDown size={16} />
                  </ActionIcon>
                </Group>
              }
              onClick={() => setIsSelectingFramework(true)}
              style={{ cursor: 'pointer' }}
            />

            {isSelectingFramework && (
              <div id="portal-framework-selector">
                <APortal
                  idParent="portal-framework-selector"
                  onClose={() => setIsSelectingFramework(false)}
                >
                  <ObjectSelector
                    typeSelectedObject={frameworkType?.key || 'framework'}
                    onSelectValue={(value) => {
                      setSelectedFramework(value);
                      setIsSelectingFramework(false);
                    }}
                    isDisplaySummary={false}
                    isDisplayTitle={true}
                    isDisplayConfirm={false}
                    height={400}
                  />
                </APortal>
              </div>
            )}
          </div>
        </InputWrapper>
      )}

      <Dropzone
        styles={{
          root: {
            height: '100%',
          },
        }}
        onDrop={(files) => setFile(files[0])}
        accept={(MIME_TYPES.xls, MIME_TYPES.xlsx)}
        multiple={false}
      >
        {(status) => dropzoneChildren(status, theme, file)}
      </Dropzone>

      <Group position="right">
        <Button
          onClick={nextStep}
          disabled={disableNextStep || isLoading}
          loading={isLoading}
        >
          Next step
        </Button>
      </Group>
    </Stack>
  );
}

export default UpFileTab;
